import React from "react";
import { Document, Page, Text, View, Image, StyleSheet } from "@react-pdf/renderer";
import { format } from "date-fns";

const statusColors: Record<string, string> = {
    acknowledged: "#4caf50",
    pending: "#ff9800",
    duplicate: "#2196f3",
    rejected: "#f44336",
};

const styles = StyleSheet.create({
    page: { padding: 30, fontSize: 12, fontFamily: "Helvetica" },
    headerTitle: { fontSize: 20, marginBottom: 20, fontWeight: "normal" },
    sectionTitle: { fontSize: 14, marginBottom: 8, fontWeight: "normal" },
    statusRow: { flexDirection: "row", alignItems: "center", marginBottom: 12 },
    statusBox: { padding: 5, borderRadius: 4, color: "white", fontSize: 10, marginRight: 10 },
    row: { flexDirection: "row", marginBottom: 6 },
    label: { width: 160, fontSize: 11, fontWeight: "bold" },
    value: { flex: 1, fontSize: 11 },
    banner: { width: "100%", height: "auto", marginBottom: 20 },
    attachmentsContainer: { flexDirection: "row", flexWrap: "wrap", marginTop: 10 },
    attachmentImage: { width: 180, height: 120, marginRight: 10, marginBottom: 10 },
    noAttachmentText: { fontSize: 11, color: "gray", marginTop: 10 }
});

const DataRow = ({ label, value }: { label: string; value: string }) => (
    <View style={styles.row}>
        <Text style={styles.label}>{label}</Text>
        <Text style={styles.value}>{value}</Text>
    </View>
);

const OtherVenueReportPDF: React.FC<{ report: any }> = ({ report }) => {
    const deputy = report.user;
    const attachments = report.attachments || [];
    const images = attachments.filter((item: any) => item.type === "IMAGE").slice(0, 4);
    const statusColor = statusColors[report.status.toLowerCase()] || "grey";

    // Dynamic additional fields
    const renderDynamicFields = () => {
        if (report.report_type === "Others") {
            return <DataRow label="Site URL" value={report.additional_info} />;
        }

        if (report.report_type === "Public Utility Vehicle") {
            return (
                <>
                    <DataRow label="Transport Company" value={report.additional_info} />
                    <DataRow label="Plate Number" value={report.puv_plate_no} />
                    <DataRow label="Route" value={report.puv_route} />
                </>
            );
        }

        if (
            report.report_type === "Video Publicity Materials" ||
            report.report_type === "Non-Video Publicity Materials"
        ) {
            return <DataRow label="Subtype" value={report.additional_info} />;
        }

        return null;
    };

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <Image src={`${process.env.PUBLIC_URL}/pdf-banner.jpg`} style={styles.banner} />

                <Text style={styles.headerTitle}>Other Venue Report</Text>

                <View style={styles.statusRow}>
                    <Text style={{ ...styles.statusBox, backgroundColor: statusColor }}>
                        {report.report_status}
                    </Text>
                    <Text
                        style={{
                            fontSize: 11,
                            color: report.has_violation ? "red" : "grey",
                            fontWeight: "bold"
                        }}
                    >
                        {report.has_violation ? "With Violation" : "Without Violation"}
                    </Text>
                </View>

                <Text style={styles.sectionTitle}>Report Details</Text>
                <DataRow label="Report ID:" value={report.id} />
                <DataRow
                    label="Date and Time of Report:"
                    value={format(new Date(report.created_at), "MMMM dd, yyyy - h:mm a")}
                />
                <DataRow label="Report Type:" value={report.report_type} />
                <DataRow label="Location:" value={report.location} />
                {renderDynamicFields()}

                <Text style={[styles.sectionTitle, { marginTop: 12 }]}>Deputy Info</Text>
                <DataRow label="Fullname:" value={`${deputy.name} ${deputy.last_name}`} />
                <DataRow label="Control Number:" value={deputy.deputy_control_number} />
                <DataRow label="Email:" value={deputy.email} />
                <DataRow label="Contact:" value={deputy.phone_number} />

                <Text style={[styles.sectionTitle, { marginTop: 12 }]}>Admin Info</Text>
                <DataRow
                    label="Fullname:"
                    value={report.admin ? `${report.admin.name} ${report.admin.last_name}` : "None"}
                />
                <DataRow label="Status:" value={report.status} />

                <Text style={[styles.sectionTitle, { marginTop: 12 }]}>Brief Description</Text>
                <Text style={{ fontSize: 11, marginBottom: 10 }}>
                    {report.description || "N/A"}
                </Text>

                <Text style={styles.sectionTitle}>Attachments</Text>
                {images.length > 0 ? (
                    <View style={styles.attachmentsContainer}>
                        {images.map((image: any, index: number) => (
                            <Image key={index} src={image.file_url} style={styles.attachmentImage} />
                        ))}
                    </View>
                ) : (
                    <Text style={styles.noAttachmentText}>No attachments available.</Text>
                )}
            </Page>
        </Document>
    );
};

export default OtherVenueReportPDF;
